/* Batch 15 · N6 (F-120) — a restored package is missing the client ledger and the client files. The note no longer sits
   in the sidebar header: it is a banner at the top of the page content, dismissed per browser session (per engagement),
   with the missing files listed on click. */
import React, { useEffect, useState } from "react";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { AlertTriangle, X } from "lucide-react";

const KEY = (id) => `azex_restore_note_dismissed_${id}`;

export default function RestoreMissingBanner() {
  const { t } = useI18n();
  const { current } = useEngagement();
  const miss = current?.package_restore_missing;
  const [hidden, setHidden] = useState(true);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!current?.id || !miss) { setHidden(true); return; }
    try { setHidden(sessionStorage.getItem(KEY(current.id)) === "1"); } catch { setHidden(false); }
    setOpen(false);
  }, [current?.id, miss]);
  if (!current?.id || !miss || hidden) return null;
  const dismiss = () => { try { sessionStorage.setItem(KEY(current.id), "1"); } catch { /* private window */ } setHidden(true); };
  const files = Array.isArray(miss.files) ? miss.files : [];
  return (
    <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" data-testid="restore-missing-banner">
      <div className="flex flex-wrap items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span className="font-medium">{t("pkg.missingBanner")}{miss.files_count ? ` (${miss.files_count})` : ""}</span>
        {files.length > 0 && (
          <button type="button" className="underline" onClick={() => setOpen((v) => !v)} data-testid="restore-missing-toggle">
            {open ? t("pkg.hideFiles") : t("pkg.showFiles")}
          </button>
        )}
        <button type="button" className="ms-auto rounded p-0.5 hover:bg-amber-100" onClick={dismiss} title={t("pkg.dismissNote")} data-testid="restore-missing-dismiss">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {open && (
        <ul className="mt-2 max-h-48 list-disc overflow-auto ps-5" data-testid="restore-missing-files">
          {files.map((f, i) => <li key={i}>{typeof f === "string" ? f : (f.name || f.filename || f.path || JSON.stringify(f))}{f && typeof f === "object" && f.size ? ` · ${f.size}` : ""}</li>)}
        </ul>
      )}
    </div>
  );
}
